import React, { useEffect, useState } from 'react';

import { useAppSelector } from '../app/hooks';
import { getActiveFeatureFlags } from '../services/featureFlags';
import { ActiveFeatureFlag } from '../interfaces/featureFlag.interface';

/**
 * Central registry of client-gated feature-flag codenames. Add a flag's
 * codename here once and reference the constant everywhere, so gating is
 * typo-proof and greppable.
 */
export const FEATURE_FLAGS = {
  V2_COMPANY_PROFILE: 'can_view_v2_company_profile',
  DARK_THEME: 'can_view_dark_theme',
} as const;

export type FeatureFlagCodename = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

// App-wide cache of the active (in-window) flags + their type targeting.
// Fetched once and refreshed on window focus — so a flag/targeting change shows
// up for the already-logged-in user without a re-login.
let cache: ActiveFeatureFlag[] | null = null;
let inflight: Promise<void> | null = null;
const subscribers = new Set<(flags: ActiveFeatureFlag[]) => void>();

export function refreshActiveFeatureFlags(): Promise<void> {
  if (!inflight) {
    inflight = getActiveFeatureFlags()
      .then((flags) => {
        cache = flags;
        subscribers.forEach((cb) => cb(flags));
      })
      .catch(() => undefined)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** Test-only: clears the module-level active-flags cache between cases. */
export function resetActiveFeatureFlagsCache(): void {
  cache = null;
  inflight = null;
  subscribers.clear();
}

function useActiveFeatureFlags(): ActiveFeatureFlag[] | null {
  const [flags, setFlags] = useState<ActiveFeatureFlag[] | null>(cache);
  useEffect(() => {
    const onChange = (next: ActiveFeatureFlag[]) => setFlags(next);
    subscribers.add(onChange);
    refreshActiveFeatureFlags();
    const onFocus = () => refreshActiveFeatureFlags();
    window.addEventListener('focus', onFocus);
    return () => {
      subscribers.delete(onChange);
      window.removeEventListener('focus', onFocus);
    };
  }, []);
  return flags;
}

/**
 * Like {@link useFeatureFlag} but also reports `loaded` — whether the active
 * flags have been fetched yet. Useful for avoiding a flash when the gate drives
 * something page-wide (e.g. the theme): callers can hold a cached value until
 * `loaded` is true.
 */
export function useFeatureFlagState(codename: string): { enabled: boolean; loaded: boolean } {
  const flags = useActiveFeatureFlags();
  const { user } = useAppSelector((state) => state.user);

  // Individually-assigned permission always counts (known synchronously).
  if (user?.permissions?.includes(codename)) return { enabled: true, loaded: true };

  if (!flags) return { enabled: false, loaded: false };
  const flag = flags.find((f) => f.codename === codename);
  const enabled = !!flag && (flag.isPublic || (!!user?.type && flag.targetUserTypes.includes(user.type)));
  return { enabled, loaded: true };
}

/**
 * True when the current user should see the given client feature: the codename
 * is in their permission list (individual assignment), the flag is public, or
 * the flag targets their `user.type`. Fail-closed while flags are loading.
 *
 *   const showV2 = useFeatureFlag(FEATURE_FLAGS.V2_COMPANY_PROFILE);
 */
export function useFeatureFlag(codename: string): boolean {
  return useFeatureFlagState(codename).enabled;
}

interface FeatureGateProps {
  flag: string;
  children: React.ReactNode;
  /** Rendered when the user lacks the flag (defaults to nothing). */
  fallback?: React.ReactNode;
}

/**
 * Declarative gate — renders `children` only when the user has the flag.
 *
 *   <FeatureGate flag={FEATURE_FLAGS.V2_COMPANY_PROFILE} fallback={<Legacy />}>
 *     <BetaThing />
 *   </FeatureGate>
 */
export function FeatureGate({ flag, children, fallback = null }: FeatureGateProps) {
  return <>{useFeatureFlag(flag) ? children : fallback}</>;
}
